import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useWhiteLabelBranding } from '@/components/WhiteLabelBrandingProvider';
import {
  DEFAULT_SOCIAL_IMAGE,
  ORGANIZATION_LOGO,
  WEBSITE_ID,
  organizationRef,
  serializeSchema,
  toHeadline,
  toIsoDate,
} from '@/lib/structuredData';

interface SEOHeadProps {
  title: string;
  description: string;
  canonical: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  ogTitle?: string;
  ogDescription?: string;
  hideBranding?: boolean;
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    tags?: string[];
  };
  faqs?: Array<{ question: string; answer: string }>;
  /** Use quando a página já renderiza um schema Article/BlogPosting dedicado. */
  skipArticleSchema?: boolean;
  eventLocationLabel?: string | null;
  eventDateLabel?: string | null;
  showStepEvent?: boolean;
  showStepDatetime?: boolean;
}


/**
 * Tags estáticas do index.html que também são gerenciadas por rota.
 * Elas permanecem no HTML como fallback para crawlers sem JS, mas precisam
 * ser removidas assim que o Helmet injeta a versão específica da página —
 * caso contrário o documento fica com duas descriptions / dois og:title,
 * e os crawlers costumam usar a primeira (a genérica da Home).
 */
const MANAGED_STATIC_SELECTORS = [
  'meta[name="description"]',
  'meta[name="title"]',
  'meta[property="og:title"]',
  'meta[property="og:description"]',
  'meta[property="og:url"]',
  'meta[property="og:type"]',
  'meta[property="og:image"]',
  'meta[property="og:image:type"]',
  'meta[property="og:image:width"]',
  'meta[property="og:image:height"]',
  'meta[property="og:image:alt"]',
  'meta[name="twitter:title"]',
  'meta[name="twitter:description"]',
  'meta[name="twitter:url"]',
  'meta[name="twitter:image"]',
  'meta[name="twitter:image:alt"]',
  'link[rel="canonical"]',
];

const DEFAULT_OG_IMAGE = DEFAULT_SOCIAL_IMAGE;

const SEOHead = ({
  title,
  description,
  canonical,
  ogImage,
  ogType = 'website',
  ogTitle,
  ogDescription,
  hideBranding = false,
  article,
  faqs,
  skipArticleSchema = false,
  eventLocationLabel,
  eventDateLabel,
  showStepEvent,
  showStepDatetime,
}: SEOHeadProps) => {
  const branding = useWhiteLabelBranding();
  const isWhiteLabel = branding.isWhiteLabel;
  const noAmeupBranding = hideBranding || isWhiteLabel;
  const fullTitle = noAmeupBranding ? title : (title.includes('AmeUp') ? title : `${title} | AmeUp`);
  const siteName = branding.supplierName || (noAmeupBranding ? 'Portal' : 'AmeUp');
  const resolvedOgTitle = ogTitle || fullTitle;
  const resolvedOgDescription = ogDescription || description;
  // Em host white-label nunca caímos na imagem social da AmeUp.
  const resolvedOgImage =
    ogImage || (isWhiteLabel ? branding.logoUrl || '' : DEFAULT_OG_IMAGE);

  // Remove as duplicatas estáticas do index.html (as do Helmet têm data-rh="true").
  useEffect(() => {
    const id = window.setTimeout(() => {
      MANAGED_STATIC_SELECTORS.forEach((selector) => {
        document.head
          .querySelectorAll<HTMLElement>(selector)
          .forEach((el) => {
            if (!el.hasAttribute('data-rh')) el.remove();
          });
      });

      // Special handling for dynamic favicon cleanup
      if (ogImage) {
        document.head
          .querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]')
          .forEach((el) => {
            if (!el.hasAttribute('data-rh')) el.remove();
          });
      }
    }, 0);
    return () => window.clearTimeout(id);
  }, [fullTitle, description, canonical, resolvedOgTitle, resolvedOgDescription, ogType, ogImage]);

  const publishedIso = toIsoDate(article?.publishedTime);
  const modifiedIso = toIsoDate(article?.modifiedTime) || publishedIso;

  // Article: todos os campos obrigatórios do Google (headline, image,
  // datePublished, author, publisher.logo) preenchidos e sem valores vazios.
  const articleSchema = serializeSchema({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: toHeadline(ogTitle || title),
    description,
    image: [resolvedOgImage],
    datePublished: publishedIso,
    dateModified: modifiedIso,
    inLanguage: 'pt-BR',
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
    author: { '@type': 'Person', name: article?.author || 'AmeUp' },
    publisher: {
      '@type': 'Organization',
      name: 'AmeUp',
      url: 'https://ameup.com.br',
      logo: ORGANIZATION_LOGO,
    },
  });

  // SoftwareApplication: schema para a plataforma
  const softwareSchema = serializeSchema({
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'AmeUp',
    operatingSystem: 'Web',
    applicationCategory: 'BusinessApplication',
    description: `AmeUp é uma plataforma de gestão de eventos focada em buffets e organizadores. ${description}`,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'BRL',
    },
  });

  // WebPage por rota: leve, válida e referenciando as entidades globais.
  const webPageSchema = noAmeupBranding
    ? null
    : serializeSchema({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name: resolvedOgTitle,
        description: resolvedOgDescription,
        inLanguage: 'pt-BR',
        isPartOf: { '@id': WEBSITE_ID },
        publisher: organizationRef,
        primaryImageOfPage: { '@type': 'ImageObject', url: resolvedOgImage },
      });

  const validFaqs = (faqs || []).filter(
    (faq) => faq?.question?.trim() && faq?.answer?.trim(),
  );

  const faqSchema = validFaqs.length > 0
    ? serializeSchema({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: validFaqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question.trim(),
          acceptedAnswer: { '@type': 'Answer', text: faq.answer.trim() },
        })),
      })
    : null;



  
  return (
    <Helmet>
      {/* Dynamic Favicon (o type real é inferido pela extensão do arquivo) */}
      {ogImage && <link rel="icon" href={ogImage} />}
      {ogImage && <link rel="shortcut icon" href={ogImage} />}
      {ogImage && <link rel="apple-touch-icon" href={ogImage} />}
      
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={resolvedOgTitle} />
      <meta property="og:description" content={resolvedOgDescription} />
      {resolvedOgImage && <meta property="og:image" content={resolvedOgImage} />}
      {resolvedOgImage && <meta property="og:image:alt" content={resolvedOgTitle} />}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="pt_BR" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={resolvedOgTitle} />
      <meta name="twitter:description" content={resolvedOgDescription} />
      {resolvedOgImage && <meta name="twitter:image" content={resolvedOgImage} />}
      {resolvedOgImage && <meta name="twitter:image:alt" content={resolvedOgTitle} />}

      {/* Article specific meta tags */}
      {ogType === 'article' && article && (
        <>
          {article.publishedTime && (
            <meta property="article:published_time" content={article.publishedTime} />
          )}
          {article.author && (
            <meta property="article:author" content={article.author} />
          )}
          {article.tags && article.tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Schema.org: Article (blog e conteúdos editoriais).
          Só é emitido quando NÃO existe um BlogPostSchema dedicado na página,
          para evitar duas entidades Article/BlogPosting no mesmo documento. */}
      {ogType === 'article' && article && !skipArticleSchema && articleSchema && (
        <script type="application/ld+json">{articleSchema}</script>
      )}

      {/* Schema.org: WebPage por rota, referenciando a Organization/WebSite
          declaradas uma única vez no index.html. */}
      {ogType === 'website' && webPageSchema && (
        <script type="application/ld+json">{webPageSchema}</script>
      )}

      {ogType === 'website' && !noAmeupBranding && softwareSchema && (
        <script type="application/ld+json">{softwareSchema}</script>
      )}

      {faqSchema && (
        <script type="application/ld+json">{faqSchema}</script>
      )}

    </Helmet>
  );
};

export default SEOHead;
